import { useConnectSlack, useDisconnectSlack, useSlack } from '../hooks/use-slack.js';
import { SectionAction, SettingsSection } from './settings-section.js';

// Slack of a server (ADR-039 section 8): the operator's account in this client's workspace, once its admin allowed the
// app. aidev reads the conversations he is in to find what waits on him; it sends nothing without his click.

/** What a scope added since the connection gives, said to the operator */
const WHY: Record<string, string> = { 'users:read.email': 'the address of who writes to you, to link their Slack account to a person' };

export function SlackConnection({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, error, isLoading } = useSlack(serverId);
  const connect = useConnectSlack(serverId);
  const disconnect = useDisconnectSlack(serverId);
  const c = data?.connection;
  const action = (label: string) => (isAdmin ? <SectionAction onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? 'Opening Slack…' : label}</SectionAction> : null);
  let state: string;
  let tone: 'normal' | 'muted' | 'warn' | 'error' = 'normal';
  let button = null;
  if (error) {
    state = `Slack state not read: ${(error as Error).message}`;
    tone = 'error';
  } else if (!data?.configured) {
    state = 'Not available yet: the Slack app of aidev is set up once (docs/slack.md).';
    tone = 'muted';
  } else if (c?.status === 'revoked') {
    state = `Slack refused the connection to ${c.teamName}: messages are no longer read. Reconnect, or ask the workspace admin whether the app was removed.`;
    tone = 'warn';
    button = action('Reconnect Slack');
  } else if (c && data?.missingScopes?.length) {
    const what = data.missingScopes.map((s) => WHY[s] ?? s).join(', ');
    state = `Connected to ${c.teamName}. Slack now asks for more: ${what}. Reconnect to give it; nothing read is lost.`;
    tone = 'warn';
    button = action('Reconnect Slack');
  } else if (c) {
    state = `Connected to ${c.teamName}. aidev reads the conversations you are in; it sends nothing without your click.`;
  } else {
    state = 'Connect your account in this client\'s Slack, once its admin allowed the app: what waits on you there comes to To do.';
    tone = 'muted';
    button = action('Connect Slack');
  }
  return (
    <SettingsSection id="slack-title" title="Slack" state={state} tone={tone} loading={isLoading} action={button}>
      {c && isAdmin && (
        <button type="button" onClick={() => disconnect.mutate()} disabled={disconnect.isPending}
          className="mt-2 rounded-sm text-xs hover:underline focus-visible:outline focus-visible:outline-2 disabled:opacity-60" style={{ color: 'var(--color-text-3)' }}>
          {disconnect.isPending ? 'Disconnecting…' : 'Disconnect Slack'}
        </button>
      )}
      {(connect.error || disconnect.error) && (
        <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{((connect.error || disconnect.error) as Error).message}</p>
      )}
    </SettingsSection>
  );
}
